import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Stack,
} from '@mui/material';

import { EditButtons } from '@barboxx/admin-components';
import { PRICE_TIER_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { PriceTier } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

interface Props {
    priceTier: PriceTier.DetailsResponse;
}

export const PriceTierDetailsCard = ({ priceTier }: Props) => {
    const { ...rest } = priceTier;
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const formContext = useForm<PriceTier.UpdateRequest>({
        resolver: yupResolver(PRICE_TIER_SCHEMA),
        defaultValues: rest,
    });

    const mutateUser = useMutation({
        mutationKey: ['priceTier', priceTier.id],
        mutationFn: (data: PriceTier.UpdateRequest) =>
            Api.Core.PriceTier.putPriceTier(priceTier.id, data),
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: PriceTier.UpdateRequest) => {
        return mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated price tier!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating price tier!', {
                    severity: 'error',
                });
            });
    };

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <Card>
                <CardHeader title="Price Tier Details" />
                <CardContent>
                    <Stack gap={2}>
                        <TextFieldElement
                            fullWidth
                            name="name"
                            disabled={!editable}
                            label="Name"
                        />
                        <TextFieldElement
                            fullWidth
                            name="description"
                            disabled={!editable}
                            label="Description"
                        />
                        <TextFieldElement
                            fullWidth
                            name="displayOrder"
                            disabled={!editable}
                            label="Display Order"
                            type="number"
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    max: 1000,
                                    step: 1,
                                },
                            }}
                        />
                    </Stack>
                </CardContent>
                <CardActions
                    sx={{
                        justifyContent: 'flex-end',
                        paddingRight: 3,
                        paddingBottom: 3,
                    }}
                >
                    <Can I="update" a="PriceTier">
                        <EditButtons
                            formContext={formContext}
                            handleCancel={handleCancel}
                            editable={editable}
                            editableActions={editableActions}
                        />
                    </Can>
                </CardActions>
            </Card>
        </FormContainer>
    );
};
